import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import {
  DATE_FORMAT_TABLE_SHORT,
  DATETIME_FORMAT_TABLE_SHORT,
} from "../../../../constants/date";
import { formatCurrency } from "@/utils/currency";
import { Button, Space, Tag, Typography } from "antd";
import { Expense } from "@/types/expense";
import { getExpenseTypeColor } from "../../RecordExpense/utils/expenseDrafts";
import { formatQuantity } from "@/utils/numbers";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { red } from "@ant-design/colors";
import { ColumnsProps } from "@/components/table";

export const getColumns = ({
  onEdit,
  onDelete,
  filterOptions,
  filterValues,
  getSortOrder,
}: ColumnsProps<Expense>): ColumnsType<Expense> => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (date: string) => dayjs(date).format(DATE_FORMAT_TABLE_SHORT),
    sorter: true,
    sortOrder: getSortOrder?.("date"),
  },
  {
    title: "Type",
    dataIndex: ["expense_type", "name"],
    key: "expense_type_id",
    render: (_: string, record: Expense) => {
      const expenseName = record?.expense_type?.name;
      const color = getExpenseTypeColor(expenseName || "");
      return <Tag color={color}>{expenseName}</Tag>;
    },
    sorter: true,
    sortOrder: getSortOrder?.("expense_type.name"),
    filters: filterOptions?.expense_type_id,
    filterSearch: true,
    filteredValue: filterValues?.expense_type_id || null,
  },
  {
    title: "Description",
    dataIndex: ["description", "name"],
    key: "description_id",
    sorter: true,
    sortOrder: getSortOrder?.("description.name"),
    filters: filterOptions?.description_id,
    filterSearch: true,
    filteredValue: filterValues?.description_id || null,
    render: (_: string, record: Expense) => {
      if (!record.description) {
        return "-";
      }

      const isActive = record.description.is_active;
      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} {record.description.name}
        </>
      );
    },
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    sorter: true,
    sortOrder: getSortOrder?.("price"),
    render: (price: number) => formatCurrency(price),
  },
  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    sorter: true,
    sortOrder: getSortOrder?.("quantity"),
    render: (quantity: number, record: Expense) =>
      quantity
        ? `${formatQuantity(quantity, 2)} ${record.unit?.symbol || ""}`
        : "-",
  },
  {
    title: "Supplier",
    dataIndex: ["supplier", "name"],
    key: "supplier_id",
    render: (_: string, record: Expense) => {
      if (!record.supplier) {
        return "-";
      }

      const isActive = record.supplier.is_active;
      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} {record.supplier.name}
        </>
      );
    },
    sorter: true,
    sortOrder: getSortOrder?.("supplier.name"),
    filters: filterOptions?.supplier_id,
    filterSearch: true,
    filteredValue: filterValues?.supplier_id || null,
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    sorter: true,
    sortOrder: getSortOrder?.("created_at"),
    render: (date: Dayjs) => dayjs(date).format(DATETIME_FORMAT_TABLE_SHORT),
  },
  {
    title: "Actions",
    key: "actions",
    render: (_: any, record: Expense) => (
      <Space>
        <Button
          icon={<EditOutlined />}
          onClick={() => onEdit?.(record)}
          type="link"
        />
        <Button
          icon={<DeleteOutlined />}
          onClick={() => onDelete?.(record)}
          type="link"
          danger
        />
      </Space>
    ),
    fixed: "right" as const,
  },
];
